import { Injectable } from '@angular/core';
import { EMPTY, from, Observable } from 'rxjs';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Global } from '../../shared/global';
import { shareReplay, switchMap } from 'rxjs/operators';
import {
  IKfzFahrzeugartResult,
  IKfzMarkenResult,
  IKfzAntriebsartResult,
  IZahlungsartResult,
  IFahrzeugarten,
  ISelbstbehaltVarianten,
  ISelbstbehaltVariantenResult,
  IBmStufenResult, IKaskoVarianten,
  IKaskoVariantenResult,
  IInsassenVarianten,
  IInsassenVariantenResult,
  IVersicherungssummeVarianten,
  IVersicherungssummeVariantenResult,
  IBezirkeResult,
  IPersonenartenResult,
  ILdstellenResult,
  IKfzMarken,
  IKfzVerwendungsbestimmungResult,
  IOesterreichischeVuResult,
  ISelbstbehalthoehen
} from '../models/lookupvalues';
import { IKfzProdukt } from '../models/kfzprodukt';
import { IKfzResult } from '../models/kfzresult';
import { IRsProdukt } from '../models/rsprodukt';
import { IRsLeistungsuebersicht } from '../models/rsleistungsuebersicht';
import { IRsPaket } from '../models/rspaket';
import { IHvPaketResult } from '../models/hvpaketresult';
import { IHvResult } from '../models/hvresult';
import { IHvProdukt } from '../models/hvprodukt';
import { IHvZusatzpakete } from '../models/hvzusatzpakete';
import { IEuroTaxMappingResult } from '../models/eurotaxmappingresult';
import { Router } from '@angular/router';
import { MaintainanceService } from './maintainance.service';
import { IRsResult } from '../models/rsresult';

@Injectable({
  providedIn: 'root'
})
export class CalculationService {

  private fahrzeugarten: Observable<IKfzFahrzeugartResult>;
  private verwendungsbestimmungen: Observable<IKfzVerwendungsbestimmungResult>;
  private antriebsarten: Observable<IKfzAntriebsartResult>;

  constructor(private http: HttpClient, private router: Router, private maintainanceService: MaintainanceService) { }

  calculateKfzRate(model: IKfzProdukt, query: string): Observable<IKfzResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IKfzResult>(Global.BASE_CALCULATION_ENDPOINT +
            `calculatekfzrate` + (query ? `?q=${query}` : ''), body, options);
        }
      }));
  }

  getKfzMarken(model: IKfzMarken): Observable<IKfzMarkenResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IKfzMarkenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getkfzmarken`, body, options);
        }
      }));
  }

  getZahlungsarten(): Observable<IZahlungsartResult> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IZahlungsartResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getzahlungsarten`, null, options);
        }
      }));
  }

  getKfzVerwendungsbestimmungen(): Observable<IKfzVerwendungsbestimmungResult> {
    if (!this.verwendungsbestimmungen) {
      this.verwendungsbestimmungen = this.requestKfzVerwendungsbestimmungen().pipe(
        shareReplay(1)
      );
    }
    return this.verwendungsbestimmungen;
  }

  private requestKfzVerwendungsbestimmungen() {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IKfzVerwendungsbestimmungResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getkfzverwendungsbestimmungen`, null, options);
        }
      }));
  }

  getKfzAntriebsarten(): Observable<IKfzAntriebsartResult> {
    if (!this.antriebsarten) {
      this.antriebsarten = this.requestKfzAntriebsarten().pipe(
        shareReplay(1)
      );
    }
    return this.antriebsarten;
  }

  private requestKfzAntriebsarten() {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IKfzAntriebsartResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getkfzantriebsarten`, null, options);
        }
      }));
  }

  getKfzFahrzeugarten(model: IFahrzeugarten): Observable<IKfzFahrzeugartResult> {
    if (!this.fahrzeugarten) {
      this.fahrzeugarten = this.requestKfzFahrzeugarten(model).pipe(
        shareReplay(1)
      );
    }
    return this.fahrzeugarten;
  }

  private requestKfzFahrzeugarten(model: IFahrzeugarten) {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IKfzFahrzeugartResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getkfzfahrzeugarten`, body, options);
        }
      }));
  }

  getSelbstbehaltVarianten(model: ISelbstbehaltVarianten): Observable<ISelbstbehaltVariantenResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<ISelbstbehaltVariantenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getselbstbehaltvarianten`, body, options);
        }
      }));
  }

  getSelbstbehalthoehen(model: ISelbstbehalthoehen): Observable<number[]> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<number[]>(Global.BASE_CALCULATION_ENDPOINT +
            `getselbstbehalthoehen`, body, options);
        }
      })
    );
  }

  getBmStufen(): Observable<IBmStufenResult> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IBmStufenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getbmstufen`, null, options);
        }
      }));
  }

  getKaskoVarianten(model: IKaskoVarianten): Observable<IKaskoVariantenResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IKaskoVariantenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getkaskovarianten`, body, options);
        }
      }));
  }

  getInsassenVarianten(model: IInsassenVarianten): Observable<IInsassenVariantenResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IInsassenVariantenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getinsassenvarianten`, body, options);
        }
      }));
  }

  getVersicherungssummeVarianten(model: IVersicherungssummeVarianten): Observable<IVersicherungssummeVariantenResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IVersicherungssummeVariantenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getversicherungssummevarianten`, body, options);
        }
      }));
  }

  getBezirke(): Observable<IBezirkeResult> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IBezirkeResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getbezirke`, null, options);
        }
      }));
  }

  getPersonenarten(): Observable<IPersonenartenResult> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IPersonenartenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getpersonenarten`, null, options);
        }
      }));
  }

  getLdstellen(): Observable<ILdstellenResult> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<ILdstellenResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getldstellen`, null, options);
        }
      }));
  }

  getOesterreichischeVu(): Observable<IOesterreichischeVuResult> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IOesterreichischeVuResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getoesterreichischevu`, null, options);
        }
      }));
  }

  calculateRsRate(model: IRsProdukt, query: string): Observable<IRsResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IRsResult>(Global.BASE_CALCULATION_ENDPOINT +
            `getrspraemie` + (query ? `?q=${query}` : ''), body, options);
        }
      }));
  }

  getLeistungsuebersicht(): Observable<IRsLeistungsuebersicht> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IRsLeistungsuebersicht>(Global.BASE_CALCULATION_ENDPOINT +
            `getleistungsuebersicht`, null, options);
        }
      }));
  }

  getRsPaket(paketId: number, deckungsumfangErhoehen: boolean): Observable<IRsPaket> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.get<IRsPaket>(Global.BASE_CALCULATION_ENDPOINT +
            `getrspaket?paketId=${paketId}&deckungsumfangErhoehen=${deckungsumfangErhoehen}`, options);
        }
      }));
  }

  getHvZusatzpakete(model: IHvZusatzpakete): Observable<IHvPaketResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IHvPaketResult>(Global.BASE_CALCULATION_ENDPOINT +
            `gethvzusatzpakete`, body, options);
        }
      }));
  }

  calculateHvRate(model: IHvProdukt, query: string): Observable<IHvResult> {
    const body = JSON.stringify(model);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.post<IHvResult>(Global.BASE_CALCULATION_ENDPOINT +
            `calculatehvrate` + (query ? `?q=${query}` : ''), body, options);
        }
      }));
  }

  getEurotaxMapping(vehicleTyp: number, markenName: string, antriebPrimary: string): Observable<IEuroTaxMappingResult> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    return from(this.maintainanceService.deploymentInProgess()).pipe(
      switchMap(deploymentInProgress => {
        if (deploymentInProgress) {
          this.router.navigate(['/maintenance']);
          return EMPTY;
        } else {
          return this.http.get<IEuroTaxMappingResult>(Global.BASE_CALCULATION_ENDPOINT +
            `geteurotaxmapping?vehicleTyp=${vehicleTyp}&markenName=${markenName}&antriebPrimary=${antriebPrimary}`, options);
        }
      }));
  }
}
